import { A, pipe } from "@mobily/ts-belt"
import type { CourseCalendarDate, CourseCalendarDateCustom, Unit } from "@shared/models/course"
import { createCourseDateCustomAttributes } from "@shared/models/course"
import type { SchoolDays } from "@shared/models/rotation-calendar"
import type { TransferrableDate } from "@shared/models/transferrable-date"
import { createTransferrableDate } from "@shared/models/transferrable-date"
import { isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { cloneDeep, filter, flatten, keyBy, without } from "lodash"
import { createRange } from "./unit-utils"

/**
 * Utilities for creating TransferrableDates.
 *
 * Usually you will then insert the range (possibly into a different course) using unit-utils' insertRange.
 */

export function extractLesson(cardStackId: string | null, courseDates: Array<CourseCalendarDate>) {
  if (cardStackId === null) return { transferrableArray: [], courseDatesWithoutLesson: courseDates }
  let transferrableArray = [
    createTransferrableDate({
      cardStackId: cardStackId,
      unitStart: [],
      unitEnd: [],
    }),
  ]
  let courseDatesWithoutLesson = A.map(courseDates, (courseDate) => {
    if (isCourseDateCustom(courseDate) && courseDate.attributes.cardStackId === cardStackId) {
      let cd = cloneDeep(courseDate)
      cd.attributes.cardStackId = null
      return cd
    } else {
      return courseDate
    }
  })

  return { transferrableArray, courseDatesWithoutLesson }
}

/**
 * Pull out the unit and return a transferrable array and the course dates sans that array
 *
 * @param {Number} amount
 * @param {Object} unitHash
 * @param {Array<Object>} courseDates
 * @param {Array<String>} datesOff
 * @param {Array<number>} schoolDays
 */
export function extractUnit(
  amount: number,
  unitHash: Unit,
  courseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays,
) {
  let unitStartDate = unitHash.startDate
  let unitEndDate = unitHash.endDate
  let unitIdsOnTheMove = flatten([unitHash.descendantIds, unitHash.id])

  let range = createRange(unitStartDate, unitEndDate, courseDates, datesOff, schoolDays)

  let initialAccumulator = {
    transferrableArray: new Array<TransferrableDate>(),
    modifiedDates: new Map<string, CourseCalendarDateCustom>(),
  }

  let { transferrableArray, modifiedDates } = A.reduce(
    range,
    initialAccumulator,
    (acc, courseDate) => {
      // Phase 1: Unit End
      // -----------------
      let unitStart = A.intersection(courseDate.attributes.unitStart, unitIdsOnTheMove)

      // Phase 2: Unit End
      // -----------------
      // Tricky test -- if we're dragging a unit forward that's at the end of the unit, we want to also move the parent unit
      let moveParentUnits = isMovingParentUnit(
        amount,
        unitHash.unitLength,
        unitHash.ancestorIds,
        courseDate.attributes.unitIds,
      )

      let unitEnd = moveParentUnits
        ? courseDate.attributes.unitEnd.slice(0) // take entire array including parents
        : A.intersection(courseDate.attributes.unitEnd, unitIdsOnTheMove) // take normal array of units on the move

      acc.transferrableArray.push(
        createTransferrableDate({
          cardStackId: courseDate.attributes.cardStackId,
          unitStart: unitStart,
          unitEnd: unitEnd,
        }),
      )

      acc.modifiedDates.set(courseDate.attributes.dateString, {
        id: courseDate.id,
        type: courseDate.type,
        attributes: createCourseDateCustomAttributes({
          isForcedOn: false,
          unitIds: [],
          cardStackId: null,
          time: null,
          dateString: courseDate.attributes.dateString,
          unitStart: without(courseDate.attributes.unitStart, ...unitIdsOnTheMove),
          unitEnd: moveParentUnits
            ? []
            : without(courseDate.attributes.unitEnd, ...unitIdsOnTheMove),
        }),
      })

      return acc
    },
  )

  let courseDatesWithoutUnit = A.map(courseDates, (courseDate) =>
    isCourseDateCustom(courseDate)
      ? modifiedDates.get(courseDate.attributes.dateString) || courseDate
      : courseDate,
  )

  return { transferrableArray, courseDatesWithoutUnit }
}

/**
 * This is used when pulling a range of lessons and moving them. For instance,
 * let's say you want to add a week for Spring Break and you want to move all the lessons
 * that are there. This will extract those lessons and units.
 */
export function extractRange(
  startDate: string,
  endDate: string,
  courseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays,
): {
  transferrableArray: Array<TransferrableDate>
  courseDatesWithoutRange: Array<CourseCalendarDate>
} {
  const courseDateMap = new Map<string, CourseCalendarDateCustom>(
    Object.entries(keyBy(filter(courseDates, isCourseDateCustom), "attributes.dateString")),
  )
  const transferrableArray: Array<TransferrableDate> = []
  const modifiedDates: Map<string, CourseCalendarDateCustom> = new Map()
  const schoolDaysSet = new Set(schoolDays)

  for (
    let date = dateFns.parse(startDate);
    dateFns.format(date, "YYYY-MM-DD") <= endDate;
    date = dateFns.addDays(date, 1)
  ) {
    if (!schoolDaysSet.has(dateFns.getDay(date))) {
      continue
    }

    const dateString = formatDateAsISO(date)
    const courseDate = courseDateMap.get(dateString)

    if (courseDate) {
      transferrableArray.push(
        createTransferrableDate({
          cardStackId: courseDate.attributes.cardStackId,
          unitStart: courseDate.attributes.unitStart,
          unitEnd: courseDate.attributes.unitEnd,
        }),
      )

      const cloned = cloneDeep(courseDate)
      cloned.attributes.cardStackId = null
      cloned.attributes.unitStart = []
      cloned.attributes.unitEnd = []
      cloned.attributes.unitIds = []
      modifiedDates.set(dateString, cloned)
    }
  }

  const courseDatesWithoutRange = A.map(courseDates, (courseDate) => {
    return isCourseDateCustom(courseDate)
      ? modifiedDates.get(courseDate.attributes.dateString) || courseDate
      : courseDate
  })
  return { transferrableArray, courseDatesWithoutRange }
}

export function extractRangeIncludingBlanks(
  startDate: string,
  endDate: string,
  courseDates: Array<CourseCalendarDate>,
  datesOff: Array<string>,
  schoolDays: SchoolDays,
): Array<TransferrableDate> {
  const customDatesToMove = createRange(startDate, endDate, courseDates, datesOff, schoolDays)

  return customDatesToMove.map((courseDate) =>
    createTransferrableDate({
      cardStackId: courseDate.attributes.cardStackId,
      unitStart: courseDate.attributes.unitStart,
      unitEnd: courseDate.attributes.unitEnd,
      // TODO: time, isForcedOn? Esp if you're not really moving the dates (just changing a rotation day).
    }),
  )
}

export function extractSelectedDates(
  dates: Array<CourseCalendarDate>,
  selectedDateStrings: Array<string>,
  selectedUnitIds: Array<string>,
  consolidatedCourseDatesOff: string[],
): Array<TransferrableDate> {
  return pipe(
    dates,
    A.filter(isCourseDateCustom),
    A.sortBy((date) => date.attributes.dateString),
    A.filter((date) => {
      return (
        A.includes(selectedDateStrings, date.attributes.dateString) ||
        (A.intersection(selectedUnitIds, date.attributes.unitIds).length > 0 &&
          !consolidatedCourseDatesOff.includes(date.attributes.dateString))
      )
    }),
    A.map((date) => {
      let shouldIncludeCardStack = A.includes(selectedDateStrings, date.attributes.dateString)
      return {
        cardStackId: shouldIncludeCardStack ? date.attributes.cardStackId : null,
        unitStart: A.intersection(selectedUnitIds, date.attributes.unitStart),
        unitEnd: A.intersection(selectedUnitIds, date.attributes.unitEnd),
      }
    }),
  )
}

function formatDateAsISO(date: Date) {
  return dateFns.format(date, "YYYY-MM-DD")
}

function isMovingParentUnit(
  amount: number,
  unitLength: number,
  unitAncestorIds: string[],
  dateUnitIds: string[],
) {
  return (
    amount > 0 && // if we're dragging forward
    amount < unitLength && // if we haven't dragged it past it's end
    A.intersection(dateUnitIds, unitAncestorIds).length > 0 // the date in question contains the ancestor
  )
}
